import React, { useState, useCallback, useEffect, useRef } from 'react';
import { playRange, stopAll, PRE_ROLL, POST_ROLL } from '../utils/audio';
import { splitToken } from '../utils/wordText';
import { API_BASE } from '../api';
import { useSwipe } from '../hooks/useSwipe';
import { useVersions, shownVersions } from '../hooks/useVersions';
import Waveform from './Waveform';
import { STATIC } from '../staticMode';
import alignFlags from '../data/align_flags.json';   // words the aligner suspects (duration/syllable anomaly)

const API = API_BASE;
const WINDOW_PAD = 1.5;  // seconds of source shown before/after the unit (room to extend)

// Encode the filename part of a source path (m4a names have spaces / CJK).
const encPath = (a) => { const i = a.indexOf('/'); return i < 0 ? encodeURIComponent(a) : a.slice(0, i + 1) + encodeURIComponent(a.slice(i + 1)); };

export default function SentenceView({ allSentences, sentenceIdx, onSentenceChange, playReq = null, onPlayingWord, onWordClick, getWordStatus, getTimes, setTimes, setBounds: onSetBounds, popupOpen, selWordIdx = -1, returnWordIdx = -1, code = '', paraMode = false, chapters = null, unitName = '句', dialect = 'tgdaya' }) {
  const [playing, setPlaying]             = useState(false);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  const [playTime, setPlayTime]           = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [jumping, setJumping]             = useState(false);
  const { sel } = useVersions();

  const unit   = allSentences[sentenceIdx];
  // Parallel texts under this unit, resolved against the dialect THIS panel is read in.
  const picked = shownVersions(sel, dialect, unit?.versions);
  const src    = unit?.src || unit?.audio || null;
  const origin = unit?.origin ?? 0;
  const audioUrl = src ? `${API}/audio/${encPath(src)}` : null;

  // The unit's playback range [start,end] in source seconds, editable & persisted
  // under word index -1. Falls back to the JSON start/end.
  // Bounds = first-word start .. last-word end, derived straight from the WORD times (so a stray
  // play-range :-1 override can never push the border into the segment -- this kept recurring on
  // #21/#28/#31). The :-1 override is no longer used.
  const _ws = (unit && unit.words) || [];
  const bounds = (_ws.length && getTimes)
    ? { start: getTimes(unit.id, 0, _ws[0]).start, end: getTimes(unit.id, _ws.length - 1, _ws[_ws.length - 1]).end }
    : { start: unit?.start ?? 0, end: unit?.end ?? (unit?.srcDur || 0) };
  const setBounds = useCallback((s, e) => {
    if (!unit) return;
    if (onSetBounds) onSetBounds(unit.id, s, e);      // propagates to siblings / parent
    else if (setTimes) setTimes(unit.id, -1, s, e);
  }, [unit, onSetBounds, setTimes]);

  // Words with edits applied AND shifted to source-absolute time (origin + t).
  const timedWords = useCallback((u) => (u?.words || []).map((w, i) => {
    const t = getTimes ? getTimes(u.id, i, w) : w;
    return { ...w, start: origin + t.start, end: origin + t.end };
  }), [getTimes, origin]);

  // Stable refs for the keyboard handler
  const playingRef = useRef(playing); const audioUrlRef = useRef(audioUrl);
  const boundsRef = useRef(bounds);   const unitRef = useRef(unit);
  const sentenceIdxRef = useRef(sentenceIdx); const totalRef = useRef(allSentences.length);
  const popupOpenRef = useRef(popupOpen);
  const playTimeRef = useRef(0);      // latest playback position (for pause/resume)
  const pausedAtRef = useRef(null);   // where we paused, to resume from
  playingRef.current = playing; popupOpenRef.current = popupOpen; audioUrlRef.current = audioUrl;
  boundsRef.current = bounds; unitRef.current = unit; playTimeRef.current = playTime;
  sentenceIdxRef.current = sentenceIdx; totalRef.current = allSentences.length;

  const startPlay = useCallback((url, start, end, words, pre = PRE_ROLL, post = POST_ROLL) => {
    if (!url) return;
    setPlaying(true); setActiveWordIdx(-1);
    const offset = start ?? 0;
    playRange(url, start, end,
      (relTime) => {
        const t = relTime + offset;                 // absolute source time
        setPlayTime(t);
        const idx = (words || []).findIndex(
          (w, i) => t >= w.start && (i === words.length - 1 || t < words[i + 1].start));
        setActiveWordIdx(idx);
      },
      () => { setPlaying(false); setActiveWordIdx(-1); setPlayTime(0); pausedAtRef.current = null; },
      pre, post);
  }, []);

  // Lead-in/tail-out clamped so they never cross into a neighbouring unit's
  // words: word-tiling removed the slack the fixed ±0.15s rolls landed in,
  // so a full roll on a 片 replayed the previous word / ran into the next.
  const clampRolls = useCallback((idx, bd, u) => {
    const same = (n) => n && (n.src || n.audio) === (u.src || u.audio) && (n.origin ?? 0) === (u.origin ?? 0);
    let pre = PRE_ROLL, post = POST_ROLL;
    const prev = allSentences[idx - 1], next = allSentences[idx + 1];
    const pw = same(prev) ? (prev.words || []) : null;
    if (pw && pw.length) {
      const t = getTimes ? getTimes(prev.id, pw.length - 1, pw[pw.length - 1]) : pw[pw.length - 1];
      pre = Math.max(0, Math.min(PRE_ROLL, bd.start - t.end));
    }
    const nw = same(next) ? (next.words || []) : null;
    if (nw && nw.length) {
      const t = getTimes ? getTimes(next.id, 0, nw[0]) : nw[0];
      post = Math.max(0, Math.min(POST_ROLL, t.start - bd.end));
    }
    return [pre, post];
  }, [allSentences, getTimes]);

  const seekPlay = useCallback((t) => {
    if (audioUrl) { stopAll(); startPlay(audioUrl, t, bounds.end, timedWords(unit)); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, bounds.end, unit, startPlay, timedWords]);

  // Whole-paragraph playback: each sentence is a SEPARATE clip, so chain them. The active word is
  // tracked across the concatenated word list (base offset per sentence).
  const playParts = useCallback((parts) => {
    if (!parts || !parts.length) return;
    setPlaying(true); setActiveWordIdx(-1);
    const base = []; let acc = 0;
    for (const p of parts) { base.push(acc); acc += (p.words || []).length; }
    let i = 0;
    const step = () => {
      if (i >= parts.length) { setPlaying(false); setActiveWordIdx(-1); setPlayTime(0); pausedAtRef.current = null; return; }
      const part = parts[i]; const ori = part.origin ?? 0; const ws = part.words || [];
      const url = `${API}/audio/${encPath(part.src || part.audio)}`;
      playRange(url, part.start, part.end,
        (rel) => {
          const t = rel + (part.start ?? 0);
          const li = ws.findIndex((w, k) => t >= ori + w.start && (k === ws.length - 1 || t < ori + ws[k + 1].start));
          setActiveWordIdx(li >= 0 ? base[i] + li : -1);
        },
        () => { i += 1; step(); });   // stopAll() (nav/stop) pauses -> onEnded never fires -> chain halts
    };
    step();
  }, []);

  const handlePlaySentence = useCallback(() => {
    if (playing) { stopAll(); setPlaying(false); setActiveWordIdx(-1); return; }
    if (unit && unit.parts && unit.parts.length > 1) { playParts(unit.parts); return; }   // 段 paragraph: play all sentences
    if (!audioUrl) return;
    const [pre, post] = clampRolls(sentenceIdx, bounds, unit);
    startPlay(audioUrl, bounds.start, bounds.end, timedWords(unit), pre, post);
  }, [audioUrl, bounds.start, bounds.end, playing, unit, startPlay, timedWords, playParts, clampRolls, sentenceIdx, bounds]);

  // Report the word being heard to the Reader, so a view switch lands on the EXACT
  // segment/sentence being played (maps 段's flat paragraph index back to its sentence).
  useEffect(() => {
    if (!onPlayingWord) return;
    if (!playing || activeWordIdx < 0) { onPlayingWord(null); return; }
    let sentId = unit?.sentenceId || unit?.id, wIdx = activeWordIdx;
    if (unit?.parts && unit.parts.length > 1) {
      let acc = 0;
      for (const p of unit.parts) { const n = (p.words || []).length; if (activeWordIdx < acc + n) { sentId = p.sentenceId || sentId; wIdx = activeWordIdx - acc; break; } acc += n; }
    }
    onPlayingWord(sentId ? { sentId, wIdx } : null);
  }, [playing, activeWordIdx, unit, onPlayingWord]);
  useEffect(() => () => { if (onPlayingWord) onPlayingWord(null); }, [onPlayingWord]);

  // Auto-play on ARRIVAL. The Reader stamps a fresh playReq object for every move
  // that should sound (‹/›/swipe/keyboard advance, 目錄 pick, 跳到故事 landing) and
  // nulls it on silent moves (level switches). Consuming by object identity — not
  // by index diff — is the point: a 目錄 pick lands on the passage's FIRST unit, so
  // itemIdx often stays 0 and an index-keyed effect reads it as "no change".
  const consumedReqRef = useRef(null);
  useEffect(() => {
    if (!playReq || consumedReqRef.current === playReq) return;
    consumedReqRef.current = playReq;
    // In WORD-VIEW (popup open) a unit change came from stepping words across segments -> the
    // word-focus autoplay plays just that word; don't also play the whole segment. Only segment-
    // to-segment WITHOUT the popup plays the whole segment. (Each play stopAll()s -> never two.)
    if (popupOpenRef.current) return;
    stopAll(); setPlaying(false); setActiveWordIdx(-1);
    const u = allSentences[sentenceIdx];
    if (!u) return;
    if (u.parts && u.parts.length > 1) { playParts(u.parts); return; }   // 段 paragraph: chain its sentence-clips
    if (!(u.src || u.audio)) return;
    const url = `${API}/audio/${encPath(u.src || u.audio)}`;
    const ori = u.origin ?? 0;
    const uw = u.words || [];
    const bd = (uw.length && getTimes)   // play range = first-word start .. last-word end (no :-1)
      ? { start: getTimes(u.id, 0, uw[0]).start, end: getTimes(u.id, uw.length - 1, uw[uw.length - 1]).end }
      : { start: u.start ?? 0, end: u.end ?? (u.srcDur || 0) };
    const words = (u.words || []).map((w, i) => {
      const t = getTimes ? getTimes(u.id, i, w) : w;
      return { ...w, start: ori + t.start, end: ori + t.end };
    });
    const [pre, post] = clampRolls(sentenceIdx, bd, u);
    startPlay(url, bd.start, bd.end, words, pre, post);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playReq, sentenceIdx, allSentences, startPlay]);

  // Keyboard: ← → navigate, Space play/pause
  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (popupOpenRef.current && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === ' ')) return;
      if (e.key === 'ArrowRight') {
        if (sentenceIdxRef.current < totalRef.current - 1) { pausedAtRef.current = null; stopAll(); onSentenceChange(sentenceIdxRef.current + 1); }
      } else if (e.key === 'ArrowLeft') {
        if (sentenceIdxRef.current > 0) { pausedAtRef.current = null; stopAll(); onSentenceChange(sentenceIdxRef.current - 1); }
      } else if (e.key === ' ') {
        e.preventDefault();
        if (playingRef.current) {                       // PAUSE: remember where we stopped
          pausedAtRef.current = playTimeRef.current;
          stopAll(); setPlaying(false); setActiveWordIdx(-1);
        } else {                                          // PLAY: resume from the pause point if mid-segment, else from the start
          const u = unitRef.current; const ori = u?.origin ?? 0;
          const words = (u?.words || []).map((w, i) => {
            const t = getTimes ? getTimes(u.id, i, w) : w;
            return { ...w, start: ori + t.start, end: ori + t.end };
          });
          const b = boundsRef.current; const p = pausedAtRef.current;
          const from = (p != null && p > b.start + 0.05 && p < b.end - 0.05) ? p : b.start;
          pausedAtRef.current = null;
          startPlay(audioUrlRef.current, from, b.end, words);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSentenceChange, startPlay, getTimes]);

  function handleWordClick(word, wi, e) {
    e.stopPropagation();
    // Shift = play from this word to the segment end (a deliberate play gesture); no word-view.
    if (e.shiftKey) {
      if (audioUrl) {
        const t = getTimes ? getTimes(unit.id, wi, word) : word;
        const absStart = origin + t.start;
        const words = timedWords(unit);
        // Lead-in must never reach back over the previous word: a fixed 0.15s
        // pre-roll replayed all of a short predecessor ("nii" played "ka nii").
        const prevEnd = wi > 0 ? words[wi - 1].end : 0;
        const lead = Math.max(0, Math.min(PRE_ROLL, absStart - prevEnd));
        setPlaying(true); setActiveWordIdx(wi);
        playRange(audioUrl, absStart, bounds.end,
          (relTime) => {
            const tt = relTime + absStart;
            const idx = words.findIndex((w, i) => tt >= w.start && (i === words.length - 1 || tt < words[i + 1].start));
            setActiveWordIdx(idx);
          },
          () => { setPlaying(false); setActiveWordIdx(-1); }, lead);
      }
      return;
    }
    // Plain click = focus the word (open word-view); the Reader focus-effect autoplays it.
    onWordClick(word, wi, unit.id, audioUrl, origin, e);
  }

  // 對齊 / 重算 / 比例 / 凍結 retired — the base alignment now comes from the offline
  // Whisper+hybrid pipeline; in-app boundary editing is hand-drag only (word-view).

  // Swipe nav (片/句/段): left = next item, right = prev — mirrors the ◀/▶ buttons.
  // Touches starting on the waveform/tools are ignored so a border drag isn't read
  // as a page turn. Touch-only, so mouse/trackpad on laptops is untouched. Declared
  // before the early return so the hook order stays stable.
  const swipe = useSwipe({
    ignore: '.wave, .wave-tools',
    onLeft:  () => { if (sentenceIdx < allSentences.length - 1) { stopAll(); onSentenceChange(sentenceIdx + 1); } },
    onRight: () => { if (sentenceIdx > 0) { stopAll(); onSentenceChange(sentenceIdx - 1); } },
  });

  if (!unit) return null;

  // Fixed generous source window for the strip (doesn't move as you edit bounds).
  const winStart = Math.max(0, (unit.start ?? 0) - WINDOW_PAD);
  const winEnd   = Math.min(unit.srcDur ?? (unit.end ?? 0), (unit.end ?? 0) + WINDOW_PAD);
  const axisDur  = Math.max(0.1, winEnd - winStart);
  const waveUrl  = STATIC
    ? (unit && unit.id ? `/waveforms/${code}/${encodeURIComponent(unit.id)}.png` : null)   // pre-generated, bundled
    : (src ? `${API}/waveform/${encPath(src)}?start=${winStart}&end=${winEnd}` : null);

  return (
    <div className="sentence-view" {...swipe}>
      {/* 段 is a reading view — no waveform (matches the phone). */}
      {!paraMode && waveUrl && !(unit.parts && unit.parts.length > 1) && (
        <div className="sv-wave-pane">
          <Waveform
            waveformUrl={waveUrl}
            playTime={playTime}
            words={unit.words || []}
            sentenceId={unit.id}
            getTimes={getTimes}
            setTimes={setTimes}
            selWord={selWordIdx}
            onSeek={seekPlay}
            axisStart={winStart}
            axisDur={axisDur}
            origin={origin}
            bounds={bounds}
            setBounds={setBounds}
            flashAt={0}
            locked={STATIC}
          />
        </div>
      )}
      <div className="sv-scroll">
        <div className="sv-toolrow">
          <button className={`sv-bigplay${playing ? ' playing' : ''}`} onClick={handlePlaySentence}
            disabled={!audioUrl && !(unit.parts && unit.parts.length > 1)}
            title={audioUrl ? 'Play (Space)' : ''}>
            {playing ? '⏸' : '▶'}
          </button>
          {/* Book · chapter:verse, so the reader always knows where in scripture they are
              (書名 章:節). Carried on every unit as unit.ref by build_bible.py. */}
          {unit.ref && <span className="sv-ref">{unit.ref}</span>}
          {/* Awaiting TTS: say so, so a silent play button doesn't read as a bug. */}
          {unit.noAudio && <span className="sv-noaudio">語音待補</span>}
        </div>
        <div className={`sv-text${paraMode ? ' sv-text-para' : ''}${returnWordIdx >= 0 ? ' sv-text-review' : ''}`}>
          {(() => { const _flag = alignFlags[unit.id] || []; return (unit.words || []).map((word, i) => {
            const times = getTimes ? getTimes(unit.id, i, word) : word;
            const { lead, core, trail } = splitToken(word.text);
            return (
              <React.Fragment key={i}>
                {lead && <span className="word-punct">{lead}</span>}
                {core && (
                  <span
                    className={`word status-${getWordStatus(core)}${i === activeWordIdx ? ' word-active' : ''}${i === returnWordIdx ? ' sv-review-target wordview-focus' : ''}${_flag.includes(i) ? ' word-flagged' : ''}`}
                    onClick={(e) => handleWordClick({ ...word, text: core }, i, e)}
                    title={`${times.start.toFixed(2)}s – ${times.end.toFixed(2)}s`}
                  >
                    {core}
                  </span>
                )}
                {trail && <span className="word-punct">{trail}</span>}
                {' '}
              </React.Fragment>
            );
          }); })()}
        </div>

        {/* Parallel texts chosen in 設定 (中文 / English / the other dialect), stacked under
            the verse. Every unit — 片, 段 and 句 — carries its verse's versions, so the
            reader never loses the translation by zooming in on a phrase. */}
        {unit.versions ? (
          <div className="sv-cnwrap">
            <button className="sv-cnlink" onClick={() => setShowTranslation(s => !s)}>
              {showTranslation ? '隱藏譯文' : '顯示譯文'}
            </button>
            {showTranslation && (
              picked.length ? picked.map(v => (
                <p key={v.key} className="sv-trans">
                  <span className="sv-vlabel">{v.label}</span>{v.text}
                </p>
              )) : <p className="sv-trans sv-trans-none">請在「設定 · 對照譯本」選擇要並列的譯本。</p>
            )}
          </div>
        ) : unit.translation && (
          <div className="sv-cnwrap">
            <button className="sv-cnlink" onClick={() => setShowTranslation(s => !s)}>
              {showTranslation ? '隱藏中文' : '顯示中文'}
            </button>
            {showTranslation && <p className="sv-trans">{unit.translation}</p>}
          </div>
        )}
      </div>

      <div className="sv-nav">
        <button className="sv-navbtn" onClick={() => { stopAll(); onSentenceChange(sentenceIdx - 1); }} disabled={sentenceIdx === 0}>‹</button>
        <NavPos chapters={chapters} idx={sentenceIdx} total={allSentences.length} unitName={unitName}
          open={jumping} setOpen={setJumping}
          onJump={(i) => { stopAll(); onSentenceChange(Math.min(Math.max(0, i), allSentences.length - 1)); }} />
        <button className="sv-navbtn" onClick={() => { stopAll(); onSentenceChange(sentenceIdx + 1); }} disabled={sentenceIdx === allSentences.length - 1}>›</button>
      </div>
    </div>
  );
}

// Position label ("7 · 句13/28" = chapter 7, 13th of its 28 units in this view;
// plain "13 / 509" when the text has no sections). Tapping it opens an explicit
// jump panel: a chapter dropdown (full titles) + a unit-number field + 跳轉.
function NavPos({ chapters, idx, total, unitName, open, setOpen, onJump }) {
  const has = chapters && chapters.length > 0;
  let ci = 0;
  if (has) for (let i = 0; i < chapters.length; i++) if (chapters[i].at <= idx) ci = i;
  const cntOf = (i) => ((chapters[i + 1] ? chapters[i + 1].at : total) - chapters[i].at);
  const label = has
    ? `${chapters[ci].n} · ${unitName}${idx - chapters[ci].at + 1}/${cntOf(ci)}`
    : `${idx + 1} / ${total}`;
  const [selCi, setSelCi] = useState(ci);
  const [num, setNum]     = useState('');
  const openPanel = () => { setSelCi(ci); setNum(''); setOpen(true); };
  const go = () => {
    setOpen(false);
    const n = parseInt(num, 10);
    if (has) onJump(chapters[selCi].at + (n >= 1 ? Math.min(n, cntOf(selCi)) - 1 : 0));
    else if (n >= 1) onJump(n - 1);
  };
  if (!open) {
    return <span className="sv-navpos sv-navpos-btn" title="點擊跳轉" onClick={openPanel}>{label}</span>;
  }
  return (
    <span className="sv-jumppanel">
      {has && (
        <select value={selCi} onChange={(e) => setSelCi(+e.target.value)}>
          {chapters.map((c, i) => (
            <option key={i} value={i}>{c.n}{c.label ? ` ${c.label}` : ''}</option>
          ))}
        </select>
      )}
      <input className="sv-jump" type="number" min="1" max={has ? cntOf(selCi) : total}
        placeholder={`${unitName} 1-${has ? cntOf(selCi) : total}`} value={num} autoFocus
        onChange={(e) => setNum(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') go(); if (e.key === 'Escape') setOpen(false); }} />
      <button className="sv-navbtn" onClick={go}>跳轉</button>
      <button className="sv-navbtn sv-jumpx" onClick={() => setOpen(false)}>✕</button>
    </span>
  );
}
